// apps/admin/src/pages/users/ImpersonateButton.tsx
//
// Phase 1 Task 50 — Per-row impersonate action.
//
// Per CONTEXT — impersonate redirect to merchant subdomain.
// Goals:.
//
// Visibility rules:
//   - Hidden if caller lacks `impersonate:grant` permission.
//   - Hidden if target user does not have a merchant or vendor membership
//     (Phase 1: heuristic — primaryRole signals; admin-side enrichment lands
//     in Phase 2 when membership query is added to GET /admin/users).
//
// Flow:
//   1. Click → confirm reason via prompt() (10-500 chars per API contract).
//   2. POST /api/v1/auth/impersonate/start { targetUserId, reason, expectedDurationMinutes }.
//   3. Read `redirectUrl` from response.
//   4. Browser navigates: window.location.href = redirectUrl.
//      The merchant subdomain handles ticket exchange (GET /_impersonate)
//      and sets host-only `_impersonate` cookie.

import { useState } from 'react';
import { Button, useAuth, useToast } from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';
import { permissionMatches } from '../../lib/permission-match.js';

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}
interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

function envelopeError(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error?.message ?? null;
  }
  return null;
}

interface ImpersonateStartResponse {
  ticket: string;
  redirectUrl: string;
  ticketExpiresAt: string;
  impersonateSessionId: string;
}

interface TargetUser {
  id: string;
  primaryRole: string;
  displayName: string;
}

export interface ImpersonateButtonProps {
  user: TargetUser;
}

/**
 * Heuristic — Phase 1 admin-list endpoint surfaces `primaryRole` only.
 * Membership-derived merchant/vendor flag arrives in Phase 2 (
 * `membership` query param). Until then, we permit impersonate on any row
 * EXCEPT rows whose primaryRole is admin/superadmin (denylist).
 */
function targetCanBeImpersonated(user: TargetUser): boolean {
  const denied = ['superadmin', 'admin'];
  return !denied.includes(user.primaryRole);
}

export function ImpersonateButton({ user }: ImpersonateButtonProps): JSX.Element | null {
  const { permissions } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation('users');
  const [submitting, setSubmitting] = useState(false);

  // Wildcard-aware permission check — superadmin holds `*:*` (not the literal
  // `impersonate:grant`), so a plain `.includes()` hid the button for the most
  // privileged role. Mirror the sidebar's canSee() wildcard expansion.
  const canImpersonate = permissions.some(
    (g) =>
      g === 'impersonate:grant' ||
      (g.includes('*') && permissionMatches(g, 'impersonate:grant')),
  );
  if (!canImpersonate) return null;
  if (!targetCanBeImpersonated(user)) return null;

  async function handleClick(): Promise<void> {
    const reason = window.prompt(
      t('impersonate.prompt', { name: user.displayName }),
      '',
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    if (trimmed.length < 10) {
      toast({
        title: t('impersonate.reasonTooShortTitle'),
        description: t('impersonate.reasonTooShortDesc'),
        variant: 'warning',
      });
      return;
    }

    setSubmitting(true);
    try {
      const client = apiClient();
      const { data, error, response } = await (
        client as unknown as {
          POST: (
            p: string,
            init: { body: unknown },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST('/api/v1/auth/impersonate/start', {
        body: {
          targetUserId: user.id,
          reason: trimmed,
          expectedDurationMinutes: 15,
        },
      });
      if (error || !response.ok) {
        toast({
          title: t('impersonate.failedTitle'),
          description: envelopeError(error) ?? `HTTP ${response.status}`,
          variant: 'error',
        });
        return;
      }
      const payload = unwrap<ImpersonateStartResponse>(data);
      if (!payload?.redirectUrl) {
        toast({
          title: t('impersonate.invalidResponseTitle'),
          description: t('impersonate.invalidResponseDesc'),
          variant: 'error',
        });
        return;
      }
      // Hard navigation — the merchant subdomain handles GET /_impersonate
      // → GETDEL ticket → host-only cookie → 302 /dashboard..
      window.location.href = payload.redirectUrl;
    } catch (err) {
      toast({
        title: t('impersonate.networkErrorTitle'),
        description: err instanceof Error ? err.message : t('impersonate.unknown'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={submitting}
      onClick={() => {
        void handleClick();
      }}
    >
      {t('impersonate.button')}
    </Button>
  );
}
